import Axios from 'axios';
import Vue from 'vue';
import i18n from '../../../i18n';
import { API_HOST } from '../../../config/app-const';
import { normalize, getUrlParams } from '../../../helpers/pagination';

const state = {
  authors: {
    isBusy: false,
    data: [],
    pagination: null,
    error: null
  }
};

const mutations = {
  LOAD_AUTHORS_BUSY: (state, payload) => {
    state.authors.isBusy = payload;
  },
  LOAD_AUTHORS_SUCCESS: (state, {data, pagination}) => {
    if (pagination && pagination.currentPage === 1) state.authors.data = []
    data.forEach(item => state.authors.data.push(item))
    state.authors.pagination = pagination;
    state.authors.error = null;
  },
  LOAD_AUTHORS_FAIL: (state, payload) => {
    state.authors.data = null;
    state.authors.error = payload;
  },
  MERGE_AUTHORS_BUSY: (state, payload) => {
    state.authors.isBusy = payload;
  },
  MERGE_AUTHORS_SUCCESS: (state, {data}) => {
    const mergedAuthors = data.mergedAuthors;
    const remainingAuthor = data.remainingAuthor;

    mergedAuthors.forEach(mergedId => {
      const mergedIndex = state.authors.data.findIndex(author => author.id == mergedId);
      state.authors.data.splice(mergedIndex, 1);
    });

    const totalAuthors = state.authors.data.length;
    const currentPage = Math.round(totalAuthors / 20) || 1;
    state.authors.pagination.currentPage = (currentPage * 20) < totalAuthors ? currentPage + 1 : currentPage;
    
    const authorIndex = state.authors.data.findIndex(a => a.id == remainingAuthor.id);
    if (authorIndex > -1) state.authors.data.splice(authorIndex, 1, remainingAuthor)
    state.authors.error = null;
  },
  MERGE_AUTHORS_FAIL: (state, payload) => {
    state.authors.data = null;
    state.authors.error = payload;
  },
  DELETE_AUTHORS_BUSY(state, payload) {
    state.authors.isBusy = payload;
  },
  DELETE_AUTHORS_SUCCESS(state, payload) {  
    let deletedIdAuthor = payload;
    const authorIndex = state.authors.data.findIndex(a => a.id == deletedIdAuthor);
    state.authors.data.splice(authorIndex, 1);
    state.authors.error = null;
  },
  DELETE_AUTHORS_FAIL(state, payload) {
    state.authors.error = payload;
    state.authors.data = null;
  },
  UPDATE_AUTHORS_BUSY(state, payload) {
    state.authors.isBusy = payload;
  },
  UPDATE_AUTHORS_SUCCESS(state, payload) {
    let updatedAuthor = payload.data;
    updatedAuthor.article_count = parseInt(updatedAuthor.article_count) || 0;
    let data = state.authors.data
    const authorIndex = data.findIndex(a => a.id == updatedAuthor.id);
    data[authorIndex] = updatedAuthor;
    state.authors.error = null;
  },
  UPDATE_AUTHORS_FAIL(state, payload) {
    state.authors.data = null;
    state.authors.error = payload;
  },
  UPDATE_PROJECT_BULK_AUTHORS_BUSY(state, payload) {
    state.authors.isBusy = payload;
  },
  UPDATE_PROJECT_BULK_AUTHORS_SUCCESS(state, payload) {
    let updatedAuthors = payload.data;
    updatedAuthors.forEach(updatedAuthor => {
      let authorIndex = state.authors.data.findIndex(author => author.id == updatedAuthor.id);
      if (authorIndex > -1) state.authors.data.splice(authorIndex, 1, updatedAuthor);
    });
  },
  UPDATE_PROJECT_BULK_AUTHORS_FAIL(state, payload) {
    state.authors.data = null;
    state.authors.error = payload;
  },
  BULK_DELETE_AUTHORS_BUSY(state, payload) {
    state.authors.isBusy = payload;
  },
  BULK_DELETE_AUTHORS_SUCCESS(state, payload) {  
    let deletedIdAuthor = payload;
    state.authors.data = [];
    state.authors.error = null;
  },
  BULK_DELETE_AUTHORS_FAIL(state, payload) {
    state.authors.error = payload;
    state.authors.isBusy = false;
    state.authors.data = null;
  },
};

const actions = {
  getAuthors: ({ commit, rootState }, { projectId, pagination, filters }) => {
    commit('LOAD_AUTHORS_BUSY', true);

    let url = `${API_HOST}/analysis/${projectId}/authors`;
    const query = getUrlParams({ ...pagination, search: filters });

    if (query) url += `?${query}`;

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    return Axios.get(url, config)
      .then(({data}) => {
        commit("LOAD_AUTHORS_SUCCESS", data.data);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("LOAD_AUTHORS_FAIL", error);
      })
      .finally(() => {
        commit("LOAD_AUTHORS_BUSY", false);
      });
  },

  updateAuthors: ({ commit, rootState }, { projectId, row }) => {
    commit("UPDATE_AUTHORS_BUSY", true);

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    const body = { ...row };

    return Axios.put(
      `${API_HOST}/analysis/${projectId}/authors/update`,
      body,
      config
    )
      .then(response => {
        commit("UPDATE_AUTHORS_SUCCESS", response.data);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("UPDATE_AUTHORS_FAIL", error);
      })
      .finally(() => commit("UPDATE_AUTHORS_BUSY", false));
  },

  deleteAuthors: ({ commit, rootState }, { projectUid, row }) => {
    commit("DELETE_AUTHORS_BUSY", true);

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    Axios.delete(
      `${API_HOST}/analysis/${projectUid}/authors/${row.id}/delete`,
      config
    )
      .then((response) => {
        commit("DELETE_AUTHORS_SUCCESS", response.data.data.id);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("DELETE_AUTHORS_FAIL", error);
      })
      .finally(() => commit("DELETE_AUTHORS_BUSY", false));
  },

  updateBulkAuthors: ({ commit, rootState }, { projectUid, bulkAuthors }) => {
    commit("UPDATE_PROJECT_BULK_AUTHORS_BUSY", true);

    const body = [
      ...bulkAuthors,
    ];

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    Axios.put(
      `${API_HOST}/analysis/${projectUid}/authors/bulk/update`,
      body,
      config
    )
      .then(response => {
        commit("UPDATE_PROJECT_BULK_AUTHORS_SUCCESS", response.data);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("UPDATE_PROJECT_BULK_AUTHORS_FAIL", error);
      })
      .finally(() => commit("UPDATE_PROJECT_BULK_AUTHORS_BUSY", false));
  },

  mergeAuthors: ({ commit, rootState }, { projectUid, authorId, selectedAuthors }) => {
    commit("MERGE_AUTHORS_BUSY", true);

    const body = [authorId, ...selectedAuthors];
    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };
    
    Axios.put(
      `${API_HOST}/analysis/${projectUid}/authors/merge`,
      body,
      config
    )
      .then(
        ({data}) => {
          commit("MERGE_AUTHORS_SUCCESS", data);
        }
      )
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("MERGE_AUTHORS_FAIL", error);
      })
      .finally(() => commit("MERGE_AUTHORS_BUSY", false));
  },

  bulkDeleteAuthors: ({ commit, rootState }, { projectId, author_ids, pagination, filters }) => {
    commit("BULK_DELETE_AUTHORS_BUSY", true);

    const { accessToken } = rootState.user.user.data;
    const data = [...author_ids,];
    const config = {
      headers: { Authorization: `Token token=${accessToken}` },
      data
    };

    Axios.delete(
      `${API_HOST}/analysis/${projectId}/authors/bulk_delete`,
      config
    ).then((response) => {
        commit("BULK_DELETE_AUTHORS_SUCCESS", []);
      }).catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("BULK_DELETE_AUTHORS_FAIL", error);
      });
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions
};